"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WorkingSteps } from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import { CHART_INK } from "@/lib/tool-kit/palette";
import {
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  layoutNetwork,
  PART_HEIGHT,
  PART_WIDTH,
} from "@/lib/tool-kit/physics-waves/network-layout";
import {
  analyse,
  combinations,
  equivalent,
  type Network,
} from "@/lib/tool-kit/physics-waves/resistor-network";

export interface NetworkViewStrings {
  equivalent: string;
  count: string;
  conductance: string;
  totalCurrent: string;
  totalPower: string;
  working: string;
  seriesStep: string;
  parallelStep: string;
  loadsHeading: string;
  columnResistor: string;
  columnResistance: string;
  columnVoltage: string;
  columnCurrent: string;
  columnPower: string;
  resistorName: string;
  figureLabel: string;
  figureDescription: string;
  singleNote: string;
}

export type PartSymbol = "resistor" | "capacitor";

/** A schematic of a network of two-terminal parts, each labelled with its value. */
export function NetworkFigure({
  network,
  symbol = "resistor",
  unit = "Ω",
  tag,
  figureId,
  label,
  description,
  fmt,
  sig,
}: {
  network: Network;
  symbol?: PartSymbol;
  /** Base unit written after each part's value. */
  unit?: string;
  /** Template for the small tag above each part, with {n}. */
  tag: string;
  figureId: string;
  label: string;
  description: string;
  fmt: NumberFormatter;
  sig: number;
}) {
  const schematic = layoutNetwork(network);
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-2xl"
      description={description}
      height={schematic.height}
      id={figureId}
      label={label}
      width={schematic.width}
    >
      {schematic.wires.map((wire) => (
        <line
          key={`${wire.x1}-${wire.y1}-${wire.x2}-${wire.y2}`}
          stroke={CHART_INK.text}
          strokeLinecap="round"
          strokeWidth={2}
          x1={wire.x1}
          x2={wire.x2}
          y1={wire.y1}
          y2={wire.y2}
        />
      ))}
      {schematic.parts.map((part) => (
        <g key={`${part.index}`}>
          {symbol === "resistor" ? (
            <rect
              fill="#fbf6e8"
              height={PART_HEIGHT}
              rx={4}
              stroke={CHART_INK.text}
              strokeWidth={2}
              width={PART_WIDTH - 24}
              x={part.x + 12}
              y={part.y}
            />
          ) : (
            <>
              <line
                stroke={CHART_INK.text}
                strokeWidth={3}
                x1={part.x + PART_WIDTH / 2 - 5}
                x2={part.x + PART_WIDTH / 2 - 5}
                y1={part.y + 2}
                y2={part.y + PART_HEIGHT - 2}
              />
              <line
                stroke={CHART_INK.text}
                strokeWidth={3}
                x1={part.x + PART_WIDTH / 2 + 5}
                x2={part.x + PART_WIDTH / 2 + 5}
                y1={part.y + 2}
                y2={part.y + PART_HEIGHT - 2}
              />
              <line
                stroke={CHART_INK.text}
                strokeWidth={2}
                x1={part.x + 12}
                x2={part.x + PART_WIDTH / 2 - 5}
                y1={part.y + PART_HEIGHT / 2}
                y2={part.y + PART_HEIGHT / 2}
              />
              <line
                stroke={CHART_INK.text}
                strokeWidth={2}
                x1={part.x + PART_WIDTH / 2 + 5}
                x2={part.x + PART_WIDTH - 12}
                y1={part.y + PART_HEIGHT / 2}
                y2={part.y + PART_HEIGHT / 2}
              />
            </>
          )}
          <text
            fill={CHART_INK.text}
            fontSize={12}
            textAnchor="middle"
            x={part.x + PART_WIDTH / 2}
            y={
              symbol === "resistor"
                ? part.y + PART_HEIGHT / 2 + 4
                : part.y + PART_HEIGHT + 13
            }
          >
            {withSiPrefix(fmt, part.ohms, unit, sig)}
          </text>
          <text
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="middle"
            x={part.x + PART_WIDTH / 2}
            y={part.y - 4}
          >
            {t(tag, { n: part.index + 1 })}
          </text>
        </g>
      ))}
    </ChartSvg>
  );
}

function stepLines(
  network: Network,
  strings: NetworkViewStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const ohm = (value: number) => withSiPrefix(fmt, value, "Ω", sig);
  return combinations(network).map((step) => {
    if (step.kind === "series") {
      return t(strings.seriesStep, {
        parts: step.parts.map(ohm).join(" + "),
        result: ohm(step.result),
      });
    }
    return t(strings.parallelStep, {
      parts: step.parts.map((part) => `1/${ohm(part)}`).join(" + "),
      result: ohm(step.result),
    });
  });
}

/** The figure, the headline numbers, the working and — with a supply — each load. */
export function NetworkOutcome({
  network,
  supply,
  strings,
  fmt,
  sig,
  count,
}: {
  network: Network;
  /** Volts across the whole network, or null when none was entered. */
  supply: number | null;
  strings: NetworkViewStrings;
  fmt: NumberFormatter;
  sig: number;
  count: number;
}) {
  const total = equivalent(network);
  const ohm = (value: number) => withSiPrefix(fmt, value, "Ω", sig);
  const loads = supply === null ? [] : analyse(network, supply);
  const lines = stepLines(network, strings, fmt, sig);
  return (
    <div className="space-y-4">
      <StatGrid>
        <StatCard emphasis label={strings.equivalent} value={ohm(total)} />
        <StatCard label={strings.count} value={fmt(count)} />
        <StatCard
          label={strings.conductance}
          value={withSiPrefix(fmt, 1 / total, "S", sig)}
        />
        {supply === null ? null : (
          <>
            <StatCard
              label={strings.totalCurrent}
              value={withSiPrefix(fmt, supply / total, "A", sig)}
            />
            <StatCard
              label={strings.totalPower}
              value={withSiPrefix(fmt, (supply * supply) / total, "W", sig)}
            />
          </>
        )}
      </StatGrid>
      <NetworkFigure
        description={t(strings.figureDescription, { total: ohm(total) })}
        figureId="spr-figure"
        fmt={fmt}
        label={strings.figureLabel}
        network={network}
        sig={sig}
        tag={strings.resistorName}
      />
      {lines.length > 0 ? (
        <WorkingSteps heading={strings.working} lines={lines} />
      ) : (
        <p className="text-muted-foreground text-sm">{strings.singleNote}</p>
      )}
      {loads.length > 0 ? (
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.loadsHeading}</h3>
          <DataTable
            head={[
              strings.columnResistor,
              strings.columnResistance,
              strings.columnVoltage,
              strings.columnCurrent,
              strings.columnPower,
            ]}
            rows={loads.map((load, i) => [
              t(strings.resistorName, { n: i + 1 }),
              ohm(load.ohms),
              withSiPrefix(fmt, load.volts, "V", sig),
              withSiPrefix(fmt, load.amps, "A", sig),
              withSiPrefix(fmt, load.watts, "W", sig),
            ])}
          />
        </div>
      ) : null}
    </div>
  );
}
